import {ChangeDetectorRef, Component, OnInit} from "@angular/core";
import {EntityData, provideSearchModel, SearchResult} from "@fedeltapointofsale/data-client";
import {AbstractSearchPage} from "../../../framework/entity/search/search-page/abstract-search-page";
import {
    AppItemAvailabilitySearchModel,
    ItemAvailability,
    ItemAvailabilityData
} from "../app-item-availability-search.model";
import {
    AppItemManageProductCountdownAction,
    AppItemManageProductCountdownService
} from "../app-item-manage-product-countdown.service";
import {ProductCountDownData} from "../product-count-down.data";
import {
    AppItemAvailabilityEditModalComponent
} from "../app-item-availability-edit-modal/app-item-availability-edit-modal.component";
import {LocationSearchModel} from "../../../pos/location/location-search.model";

@Component({
    selector: "wbo-app-item-availability-search-page",
    templateUrl: "./app-item-availability-search-page.component.html",
    styleUrls: ["./app-item-availability-search-page.component.scss"],
    providers: [
        provideSearchModel(AppItemAvailabilitySearchModel),
        AppItemManageProductCountdownService,
        LocationSearchModel
    ]
})
export class AppItemAvailabilitySearchPageComponent extends AbstractSearchPage implements OnInit {
    ItemAvailability = ItemAvailability;

    savingAvailabilitySellableId: number;
    savingPinSellableId: number;

    itemAvailabilityOutletGroupId: number;
    showLocation: boolean;

    constructor(public appItemManageProductCountdownService: AppItemManageProductCountdownService,
                public searchModel: AppItemAvailabilitySearchModel,
                private changeDetectorRef: ChangeDetectorRef,
                private locationSearchModel: LocationSearchModel) {
        super();
    }

    ngOnInit(): void {
        this.init();
    }

    async init(): Promise<void> {
        this.itemAvailabilityOutletGroupId = undefined;
        this.showLocation = true;
        const locationSearchResult: SearchResult = await this.locationSearchModel.retrieve();
        if (locationSearchResult.totalCount === 1) {
            this.itemAvailabilityOutletGroupId = locationSearchResult.data[0].outletGroupId;
            this.showLocation = false;
            this.searchModel.setFilterValue("locationId", this.itemAvailabilityOutletGroupId);
        } else {
            this.searchPageComponent.refresh();
        }
    }

    editCountdown(itemAvailability: ItemAvailabilityData): void {
        this.searchPageComponent.createModal(AppItemAvailabilityEditModalComponent).subscribe((modal: AppItemAvailabilityEditModalComponent) => {
            const countdowns: { [sellableId: string]: ProductCountDownData } = {};
            countdowns[itemAvailability.sellableId] = this.appItemToProductCountDown(itemAvailability);
            modal.setup(itemAvailability.sellableId, itemAvailability.name, this.itemAvailabilityOutletGroupId, countdowns);
            modal.onClose.subscribe((confirm) => {
                if (confirm) {
                    const productCountDownData: ProductCountDownData = countdowns[itemAvailability.sellableId];
                    this.save(itemAvailability, productCountDownData);
                }
                this.searchPageComponent.destroyModal(modal);
                this.changeDetectorRef.detectChanges();
            });
        });
    }

    getAvailabilityValue(itemAvailability: ItemAvailabilityData): ItemAvailability {
        return itemAvailability.notSold ? ItemAvailability.NOT_SOLD
            : itemAvailability.productCount == undefined || itemAvailability.productCount > 0 ? ItemAvailability.AVAILABLE
                : itemAvailability.soldOutToday ? ItemAvailability.SOLD_OUT_TODAY
                    : ItemAvailability.SOLD_OUT;
    }

    async updateAvailability(itemAvailability: ItemAvailabilityData, option: ItemAvailability): Promise<void> {
        const productCountDown: ProductCountDownData = this.appItemToProductCountDown(itemAvailability);
        if (option === ItemAvailability.NOT_SOLD) {
            productCountDown.notSold = true;
        } else if (option === ItemAvailability.AVAILABLE) {
            productCountDown.productCount = undefined;
            productCountDown.soldOutToday = false;
            productCountDown.notSold = false;
        } else {
            productCountDown.productCount = 0;
            productCountDown.soldOutToday = option === ItemAvailability.SOLD_OUT_TODAY;
            productCountDown.notSold = false;
        }
        await this.save(itemAvailability, productCountDown);
    }

    async save(itemAvailability: ItemAvailabilityData, productCountDown: ProductCountDownData): Promise<void> {
        const action: AppItemManageProductCountdownAction = EntityData.create(AppItemManageProductCountdownAction);
        action.countdowns = [productCountDown];
        this.savingAvailabilitySellableId = itemAvailability.sellableId;
        await this.appItemManageProductCountdownService.performAction(action);
        this.savingAvailabilitySellableId = undefined;
        itemAvailability.productCount = productCountDown.productCount;
        itemAvailability.soldOutToday = productCountDown.soldOutToday;
        itemAvailability.notSold = productCountDown.notSold;
        itemAvailability.resetAtStartOfDay = productCountDown.resetAtStartOfDay;
        itemAvailability.restockLevel = productCountDown.restockLevel;
        itemAvailability.pinned = productCountDown.pinned;

        this.searchPageComponent.setRefreshTimer();
    }

    appItemToProductCountDown(itemAvailability: ItemAvailabilityData): ProductCountDownData {
        const productCountDown: ProductCountDownData = EntityData.create(ProductCountDownData);
        productCountDown.sellableId = itemAvailability.sellableId;
        productCountDown.productCountDownId = itemAvailability.productCountdownId;
        productCountDown.resetAtStartOfDay = itemAvailability.resetAtStartOfDay;
        productCountDown.restockLevel = itemAvailability.restockLevel;
        productCountDown.locationId = this.itemAvailabilityOutletGroupId;
        productCountDown.productCount = itemAvailability.productCount;
        productCountDown.soldOutToday = itemAvailability.soldOutToday;
        productCountDown.notSold = itemAvailability.notSold;
        productCountDown.pinned = itemAvailability.pinned;
        return productCountDown;
    }

    get requiresLocationSelection(): boolean {
        return this.itemAvailabilityOutletGroupId == undefined;
    }

    itemAvailabilityLocationSelected(): void {
        this.searchModel.setFilterValue("locationId", this.itemAvailabilityOutletGroupId);
    }

    async pinItem(itemAvailability: ItemAvailabilityData): Promise<void> {
        if (!this.itemAvailabilityOutletGroupId) {
            return;
        }
        const productCountDown: ProductCountDownData = this.appItemToProductCountDown(itemAvailability);
        productCountDown.pinned = !itemAvailability.pinned;
        this.savingPinSellableId = itemAvailability.sellableId;
        try {
            await this.save(itemAvailability, productCountDown);
        } finally {
            this.savingPinSellableId = undefined;
        }
        this.searchPageComponent.refresh();
    }
}
